import { useEffect, useRef, useState } from 'react';

function _normalizeAxiosPath(input) {
  if (!input || typeof input !== 'string') return '/';
  let out = input.trim();
  if (!out.startsWith('/')) out = '/' + out;
  // Strip accidental leading /api/ because the axiosInstance baseURL already
  // ends in /api (see App.js _resolveApiBase). Without this the request becomes
  // /api/api/... which returns 404 and prints red lines to DevTools console.
  out = out.replace(/^\/api\//i, '/');
  if (out.length > 1) out = out.replace(/\/+$/, '');
  return out || '/';
}

export default function useServerTime(axiosInstance) {
  const [time, setTime] = useState(() => new Date());
  const offsetRef = useRef(0);
  const fetchedRef = useRef(false);
  const acRef = useRef(null);
  const syncCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let backoffTimer = null;

    const cleanupCurrentAbort = () => {
      if (acRef.current) {
        try {
          acRef.current.abort();
        } catch (_) {}
        acRef.current = null;
      }
    };
    cleanupCurrentAbort();
    acRef.current = new AbortController();
    const signal = acRef.current.signal;

    const fetchServerTime = async () => {
      try {
        if (cancelled) return;
        syncCountRef.current += 1;
        let serverUtcMs = null;
        const localAtResponse = Date.now();

        if (axiosInstance && typeof axiosInstance.get === 'function') {
          try {
            const safeUrl = _normalizeAxiosPath('/health');
            const res = await axiosInstance.get(safeUrl, {
              signal,
              validateStatus: () => true,
              // Tight timeout: if backend is slow/unreachable, do NOT block
              // the hook for 30 s. Fail fast to local clock fallback.
              timeout: 5000,
              // Silent: do not trigger the 401 interceptor redirect; this
              // probe must work even when no token is present yet.
              skipAuthInterceptor: true,
            });
            if (!cancelled) {
              if (res?.data && typeof res.data.server_time !== 'undefined') {
                const parsed = new Date(res.data.server_time).getTime();
                if (!Number.isNaN(parsed)) serverUtcMs = parsed;
              }
              if (!serverUtcMs && res?.headers && typeof res.headers.date === 'string') {
                const parsed = new Date(res.headers.date).getTime();
                if (!Number.isNaN(parsed)) serverUtcMs = parsed;
              }
            }
          } catch (_err) {
            // Silently ignored: backend offline / URL missing / network
            // error. All paths fall back to local clock so no user-visible
            // failure state and no unhandled rejections are possible.
            // (Red console 404s for /api/api/health were from the
            //  double-prefix bug now fixed by _normalizeAxiosPath.)
          }
        }

        if (!cancelled && serverUtcMs != null) {
          const candidate = serverUtcMs - localAtResponse;
          if (Math.abs(candidate - offsetRef.current) > 250) {
            offsetRef.current = candidate;
          }
          fetchedRef.current = true;
        }

        if (!cancelled) {
          setTime(new Date(Date.now() + offsetRef.current));
        }
      } catch (e) {
        if (cancelled || (e && e.name === 'AbortError')) return;
        offsetRef.current = 0;
        fetchedRef.current = true;
        if (!cancelled) {
          setTime(new Date(Date.now()));
        }
      }
    };

    // Retry-once exponential backoff. If backend is slow/unreachable (staging
    // has no /health endpoint, for example), we try on mount, then once after
    // 2 s, then once after 6 s; after that we only fall back to local time
    // plus long 30 min refresh. No console noise at any stage.
    const backoffSteps = [0, 2000, 6000];
    let stepIdx = 0;
    const runStep = () => {
      if (cancelled) return;
      if (stepIdx >= backoffSteps.length) return;
      if (fetchedRef.current) return;
      const wait = backoffSteps[stepIdx];
      stepIdx += 1;
      if (wait <= 0) {
        fetchServerTime().catch(() => {
          backoffTimer = setTimeout(runStep, Math.max(1, backoffSteps[stepIdx - 1] || 0));
        });
      } else {
        backoffTimer = setTimeout(() => {
          fetchServerTime().catch(() => {
            runStep();
          });
        }, wait);
      }
    };
    runStep();

    const timer = setInterval(() => {
      if (!cancelled) {
        setTime(new Date(Date.now() + offsetRef.current));
      }
    }, 1000);

    // Long, calm resync every 30 min. Server clock drift doesn't change
    // minute-to-minute and there's no value spamming the backend every 10 min
    // when 99% of users never stay on a single page that long anyway.
    const resyncTimer = setInterval(() => {
      if (!cancelled && !signal.aborted) {
        fetchServerTime().catch(() => void 0);
      }
    }, 30 * 60 * 1000);

    return () => {
      cancelled = true;
      if (backoffTimer) clearTimeout(backoffTimer);
      cleanupCurrentAbort();
      clearInterval(timer);
      clearInterval(resyncTimer);
    };
  }, [axiosInstance]);

  return time;
}
